import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { AppBarButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Add } from "@material-ui/icons";
import { useHistory } from "react-router-dom";

export const NewExpansionButtonAndDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <AppBarButton
        tooltip="Create Scope Expansion Application"
        onClick={() => setIsOpen(true)}
        Icon={Add}
      />
      {isOpen && <NewExpansionDialog onClose={() => setIsOpen(false)} />}
    </>
  );
};

type NewExpansionDialogProps = {
  onClose: () => void;
};

const NewExpansionDialog: React.FC<NewExpansionDialogProps> = ({ onClose }) => {
  const { push } = useHistory();

  const onSubmit = () => {
    push("scope/applications/new");
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md">
        <DialogTitle>
          Applying to expand your organisation's scope of recognition
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body1"
            style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
          >
            If you wish to offer qualifications at different levels or in new
            sector subject areas (SSAs), or qualifications which have specific
            conditions or criteria which are not within your current scope of
            recognition, you will need to apply using this system to extend your
            scope of your recognition. Additionally you can use this system to
            exempt qualifications from your current scope of recognition. Please
            ensure you check your existing scope of recognition before
            submitting an application to ensure any new qualification you wish
            to offer isn’t already covered by your scope. You can do this on
            your organisation page. You will be able to filter your scope by
            qualification type or search for specific words. You will be asked
            to answer some specific questions relating to what you are asking
            for. These include information relating to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.gov.uk/government/publications/types-of-regulated-qualifications/qualification-descriptions"
                >
                  The Regulatory type(s)
                </a>{" "}
                or description of qualification(s) you want. For example,
                qualifications subject only to the General Conditions of
                Recognition (other), end point assessment (EPA) standards and
                other regulatory types or something more specific;
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                The specific EPA standard(s) because we regulate each standard
                individually and how far advanced your development and any
                anticipated numbers for each;
              </Typography>
            </li>
            <li>
              <Typography variant="body1">Any additional SSA(s);</Typography>
            </li>
            <li>
              <Typography variant="body1">The level(s) you want;</Typography>
            </li>
            <li>
              <Typography variant="body1">
                Expected volume of certificates;
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                How this differs from your existing scope of recognition, for
                example will you use different assessment methods, use the same
                processes as your existing regulated qualifications, have the
                expertise and resources (people and financial) already or are
                you planning on recruiting these etc.
              </Typography>
            </li>
          </ul>
          <Typography
            variant="body1"
            style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
          >
            You can save your request as you go through the form. The guidance
            document gives you more information and there is a glossary of key
            terms embedded within the form to help. Once you submit the form we
            will review this information against our{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/885971/2020-05-19-criteria-for-recognition_pdfa.pdf"
            >
              Criteria for Recognition
            </a>{" "}
            and decide whether we need to ask you for additional evidence. This
            may be in the form of a statement of assurance for some or all of
            the Criteria or additional evidence relating to one or more of the
            Criteria. We will use this system to manage any request for
            additional information and inform you of the outcome. There is more
            information about using this system in a guidance document which you
            find <a href="/support">here</a>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
          <Button onClick={onSubmit} color="primary">
            Start Application
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
